import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import { RideWiseRow } from "./types";
import { fmtMoney, fmtNum, safeArr, toMonthKey } from "./helpers";

interface Props {
  rw: RideWiseRow[];
  loading: boolean;
}

const TotalMobilityTab: React.FC<Props> = ({ rw, loading }) => {
  const monthly = useMemo(() => {
    const m = new Map<string, { month: string; trans: number; fare_total: number; subsidy: number; passengers: number; hoist: number }>();
    for (const r of safeArr(rw)) {
      const key = toMonthKey(r.trip_date);
      if (!key) continue;
      const cur = m.get(key) || { month: key, trans: 0, fare_total: 0, subsidy: 0, passengers: 0, hoist: 0 };
      cur.trans += 1;
      cur.fare_total += Number(r.fare_total || 0);
      cur.subsidy += Number(r.concession_amount || 0);
      cur.passengers += Number(r.number_of_passengers || 0);
      if (r.hoist_deployed) cur.hoist += 1;
      m.set(key, cur);
    }
    return Array.from(m.values()).sort((a, b) => a.month.localeCompare(b.month));
  }, [rw]);

  const yoy = useMemo(() => {
    const m = new Map<string, Map<string, number>>(); // month-num -> year -> trans
    for (const r of safeArr(rw)) {
      const d = r.trip_date || "";
      if (d.length < 7) continue;
      const yr = d.slice(0, 4);
      const mn = d.slice(5, 7);
      if (!m.has(mn)) m.set(mn, new Map());
      const yMap = m.get(mn)!;
      yMap.set(yr, (yMap.get(yr) || 0) + 1);
    }
    const yearsSet = new Set<string>();
    for (const yMap of m.values()) for (const y of yMap.keys()) yearsSet.add(y);
    const years = Array.from(yearsSet).sort();
    const months = Array.from(m.keys()).sort();
    return months.map((mn) => {
      const row: any = { month: mn };
      for (const y of years) row[y] = m.get(mn)?.get(y) || 0;
      return row;
    }).concat([{ __years: years } as any]);
  }, [rw]);

  const yoyYears: string[] = (yoy.length ? (yoy[yoy.length - 1] as any).__years : []) || [];
  const yoyData = yoy.slice(0, -1) as any[];

  const summary = useMemo(() => {
    const months = monthly.length || 1;
    const totalTrans = monthly.reduce((s, r) => s + r.trans, 0);
    const totalSub = monthly.reduce((s, r) => s + r.subsidy, 0);
    const totalHoist = monthly.reduce((s, r) => s + r.hoist, 0);
    return {
      avgTrans: totalTrans / months,
      avgSubPerTrans: totalTrans ? totalSub / totalTrans : 0,
      avgHoist: totalHoist / months,
    };
  }, [monthly]);

  if (loading) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="py-12 text-center text-muted-foreground">
          Loading Total Mobility data…
        </CardContent>
      </Card>
    );
  }

  if (!monthly.length) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="py-12 text-center text-muted-foreground">
          No RideWise data in the selected period.
        </CardContent>
      </Card>
    );
  }

  const palette = ["#eab308", "#3b82f6", "#22c55e", "#ef4444", "#a855f7", "#06b6d4"];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="bg-card border border-border">
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground">Avg Transactions / Month</div>
            <div className="text-2xl font-bold">{fmtNum(summary.avgTrans, 1)}</div>
          </CardContent>
        </Card>
        <Card className="bg-card border border-border">
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground">Avg Subsidy / Transaction</div>
            <div className="text-2xl font-bold">{fmtMoney(summary.avgSubPerTrans)}</div>
          </CardContent>
        </Card>
        <Card className="bg-card border border-border">
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground">Avg Hoist / Month</div>
            <div className="text-2xl font-bold">{fmtNum(summary.avgHoist, 1)}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border border-border">
        <CardHeader>
          <CardTitle className="text-base">Monthly Total Mobility</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={monthly}>
              <CartesianGrid stroke="#444" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="#aaa" />
              <YAxis yAxisId="left" stroke="#aaa" />
              <YAxis yAxisId="right" orientation="right" stroke="#aaa" />
              <Tooltip contentStyle={{ background: "#111", border: "1px solid #333" }} />
              <Legend />
              <Bar yAxisId="left" dataKey="trans" name="Transactions" fill="#eab308" />
              <Bar yAxisId="left" dataKey="passengers" name="Passengers" fill="#3b82f6" />
              <Line yAxisId="right" dataKey="fare_total" name="Fare Total ($)" stroke="#22c55e" />
              <Line yAxisId="right" dataKey="subsidy" name="Subsidy ($)" stroke="#ef4444" />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {yoyYears.length > 1 && (
        <Card className="bg-card border border-border">
          <CardHeader>
            <CardTitle className="text-base">Year-on-Year Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={yoyData}>
                <CartesianGrid stroke="#444" strokeDasharray="3 3" />
                <XAxis dataKey="month" stroke="#aaa" />
                <YAxis stroke="#aaa" />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #333" }} />
                <Legend />
                {yoyYears.map((y, i) => (
                  <Line key={y} dataKey={y} stroke={palette[i % palette.length]} />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TotalMobilityTab;
